import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  jobApplicationApi,
  type ApplicationStatus,
  type MyApplication,
  type SurveyAnswers,
} from '../../api/job_application_api';
import { APTITUDE_QUESTIONS, GRAMMAR_OPTIONS, SAMPLE_TASKS, US_STATES } from './questions';

const STEPS = [
  { slug: 'details', label: 'About you' },
  { slug: 'sample', label: 'Sample task' },
  { slug: 'status', label: 'Review' },
];

function stepForStatus(status: ApplicationStatus): string {
  if (status === 'not-started') return 'details';
  if (status === 'more-info') return 'sample';
  return 'status';
}

const EMPTY_ANSWERS: SurveyAnswers = {
  first_name: '',
  last_name: '',
  home_street_1: '',
  home_city: '',
  home_state: '',
  home_zip: '',
  home_country: 'US',
  resume: '',
  legally_able_to_work: '',
  when_to_use_google_docs: '',
  carnagie_deli_cross_streets: '',
  finding_a_steakhouse: '',
  what_program_for_docx: '',
  theyre: '',
  there: '',
  their: '',
};

const SectionTitle = ({ children, first }: { children: React.ReactNode; first?: boolean }) => (
  <Typography variant="h6" sx={{ mt: first ? 0 : 4, mb: 2 }}>
    {children}
  </Typography>
);

function GrammarSelect({
  value,
  onChange,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
}) {
  return (
    <Select
      size="small"
      value={value}
      displayEmpty
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ 'aria-label': label }}
      sx={{ mx: 1, minWidth: 110 }}
    >
      <MenuItem value="" disabled>
        choose
      </MenuItem>
      {GRAMMAR_OPTIONS.map((o) => (
        <MenuItem key={o.value} value={o.value}>
          {o.label}
        </MenuItem>
      ))}
    </Select>
  );
}

function DetailsStep({
  mine,
  onSubmitted,
}: {
  mine: MyApplication;
  onSubmitted: (status: ApplicationStatus) => void;
}) {
  const [answers, setAnswers] = useState<SurveyAnswers>({
    ...EMPTY_ANSWERS,
    first_name: mine.first_name ?? '',
    last_name: mine.last_name ?? '',
    home_street_1: mine.home_street_1 ?? '',
    home_city: mine.home_city ?? '',
    home_state: mine.home_state ?? '',
    home_zip: mine.home_zip ?? '',
    home_country: mine.home_country || 'US',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (field: keyof SurveyAnswers) => (value: string) =>
    setAnswers((prev) => ({ ...prev, [field]: value }));

  const missing = (Object.keys(EMPTY_ANSWERS) as (keyof SurveyAnswers)[]).filter(
    (f) => f !== 'home_state' && !answers[f].trim(),
  );
  const needsState = answers.home_country === 'US' && !answers.home_state;
  const complete = missing.length === 0 && !needsState;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await jobApplicationApi.submitSurvey(answers);
      onSubmitted(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the application');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <SectionTitle first>What's your name?</SectionTitle>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="First name" fullWidth value={answers.first_name}
            onChange={(e) => set('first_name')(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField label="Last name" fullWidth value={answers.last_name}
            onChange={(e) => set('last_name')(e.target.value)} />
        </Grid>
      </Grid>

      <SectionTitle>Where do you live?</SectionTitle>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField label="Street address" fullWidth value={answers.home_street_1}
            onChange={(e) => set('home_street_1')(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField label="City" fullWidth value={answers.home_city}
            onChange={(e) => set('home_city')(e.target.value)} />
        </Grid>
        <Grid size={{ xs: 6, sm: 3 }}>
          <FormControl fullWidth size="small" disabled={answers.home_country !== 'US'}>
            <InputLabel id="apply-state">State</InputLabel>
            <Select labelId="apply-state" label="State" value={answers.home_state}
              onChange={(e) => set('home_state')(e.target.value)}>
              {US_STATES.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 6, sm: 4 }}>
          <TextField label="ZIP" fullWidth value={answers.home_zip}
            onChange={(e) => set('home_zip')(e.target.value)} />
        </Grid>
        <Grid size={12}>
          <FormControl fullWidth size="small">
            <InputLabel id="apply-country">Country</InputLabel>
            <Select labelId="apply-country" label="Country" value={answers.home_country}
              onChange={(e) => set('home_country')(e.target.value)}>
              <MenuItem value="US">United States of America</MenuItem>
              <MenuItem value="OUTSIDE_US">Other</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <SectionTitle>Resume / experience</SectionTitle>
      <TextField
        label="Paste your most recent resume"
        fullWidth
        multiline
        minRows={6}
        value={answers.resume}
        onChange={(e) => set('resume')(e.target.value)}
      />

      <SectionTitle>How do you work?</SectionTitle>
      {APTITUDE_QUESTIONS.map((q) => (
        <FormControl key={q.field} component="fieldset" sx={{ display: 'block', mb: 3 }}>
          <FormLabel component="legend" sx={{ color: 'text.primary', mb: 0.5 }}>{q.label}</FormLabel>
          <RadioGroup value={answers[q.field]} onChange={(e) => set(q.field)(e.target.value)}>
            {q.options.map((o) => (
              <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
            ))}
          </RadioGroup>
        </FormControl>
      ))}

      <FormControl component="fieldset" sx={{ display: 'block', mb: 3 }}>
        <FormLabel component="legend" sx={{ color: 'text.primary', mb: 0.5 }}>
          Are you legally able to work in the US?
        </FormLabel>
        <RadioGroup row value={answers.legally_able_to_work}
          onChange={(e) => set('legally_able_to_work')(e.target.value)}>
          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
          <FormControlLabel value="No" control={<Radio />} label="No" />
        </RadioGroup>
      </FormControl>

      <SectionTitle>Do you know your grammar?</SectionTitle>
      <Box sx={{ lineHeight: 3, color: 'text.primary' }}>
        I love those guys,
        <GrammarSelect label="first blank" value={answers.theyre} onChange={set('theyre')} />
        so fun! Look over
        <GrammarSelect label="second blank" value={answers.there} onChange={set('there')} />
        at them playing with
        <GrammarSelect label="third blank" value={answers.their} onChange={set('their')} />
        dog.
      </Box>

      {error && <Alert severity="error" sx={{ mt: 3 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 4 }}>
        {!complete && (
          <Typography variant="body2" color="text.secondary">
            Answer every question to continue
          </Typography>
        )}
        <Button type="submit" variant="contained" size="large" disabled={!complete || submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Continue'}
        </Button>
      </Box>
    </form>
  );
}

function SampleStep({ onSubmitted }: { onSubmitted: (status: ApplicationStatus) => void }) {
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const result = await jobApplicationApi.submitSample(message);
      onSubmitted(result.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not submit the sample task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box>
      <Typography color="text.secondary" sx={{ mt: 2 }}>
        Pick one of the sample tasks below and respond the same way you'd respond to a client.
      </Typography>
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {SAMPLE_TASKS.map((task, i) => (
          <Grid key={task.title} size={{ xs: 12, md: 6 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="primary">Sample task {i + 1}</Typography>
                <Typography variant="subtitle1" gutterBottom>{task.title}</Typography>
                <Typography variant="body2" color="text.secondary">{task.body}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <SectionTitle>Your reply</SectionTitle>
      <TextField
        fullWidth
        multiline
        minRows={10}
        placeholder="Write your reply here…"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />
      <Alert severity="info" icon={<InfoOutlinedIcon />} sx={{ mt: 2 }}>
        Just like when you're really helping someone, say where you found the information and why
        you're recommending it. We read a lot of applications: answers copied from a forum stand out.
      </Alert>

      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
        <Button variant="contained" size="large" onClick={submit}
          disabled={!message.trim() || submitting}>
          {submitting ? <CircularProgress size={24} /> : 'Submit application'}
        </Button>
      </Box>
    </Box>
  );
}

function StatusStep({ mine }: { mine: MyApplication }) {
  const navigate = useNavigate();
  const name = mine.first_name || 'there';

  if (mine.status === 'accept' || mine.is_assistant) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CheckCircleIcon color="success" sx={{ fontSize: 64 }} />
        <Typography variant="h5" sx={{ mt: 2 }}>Welcome aboard, {name}!</Typography>
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          Your application was accepted. Time to get started.
        </Typography>
        <Button variant="contained" sx={{ mt: 3 }} onClick={() => navigate('/assistant')}>
          Go to the assistant dashboard
        </Button>
      </Box>
    );
  }

  if (mine.status === 'pending') {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <HourglassTopIcon color="primary" sx={{ fontSize: 64 }} />
        <Typography variant="h5" sx={{ mt: 2 }}>Thanks, {name}. We have everything we need.</Typography>
        <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
          Your application is pending review. We'll email you with the decision;
          there's nothing else to do in the meantime.
        </Typography>
      </Box>
    );
  }

  const message =
    mine.status === 'failed'
      ? "After reviewing your application and sample task we've decided it's not the right fit at " +
        'this time. You are welcome to apply again.'
      : "It's not the right fit at this time, but we wish you the best of luck in the future!";

  return (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <Typography variant="h5">Thanks for applying, {name}.</Typography>
      <Typography color="text.secondary" sx={{ mt: 1, maxWidth: 520, mx: 'auto' }}>
        {message}
      </Typography>
      {mine.status === 'failed' && (
        <Button variant="outlined" sx={{ mt: 3 }} onClick={() => navigate('/apply/details')}>
          Apply again
        </Button>
      )}
    </Box>
  );
}

export const ApplyPage = observer(() => {
  const { step } = useParams<{ step?: string }>();
  const navigate = useNavigate();
  const [mine, setMine] = useState<MyApplication | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMine(await jobApplicationApi.mine());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your application');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const afterSubmit = async (status: ApplicationStatus) => {
    await load();
    navigate(`/apply/${stepForStatus(status)}`);
  };

  if (error) {
    return <Container maxWidth="md" sx={{ py: 4 }}><Alert severity="error">{error}</Alert></Container>;
  }
  if (!mine) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}><CircularProgress /></Box>;
  }

  const natural = mine.is_assistant ? 'status' : stepForStatus(mine.status);
  // Only "apply again" after a failure may revisit the details step
  const allowed = step === natural || (step === 'details' && mine.status === 'failed');
  if (!step || !allowed) {
    return <Navigate to={`/apply/${natural}`} replace />;
  }
  const current = step;
  const activeIndex = STEPS.findIndex((s) => s.slug === current);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>Work with Fancy Hands</Typography>
      <Typography color="text.secondary">
        Help busy people get things done, from home, on your own schedule.
      </Typography>

      <Stepper activeStep={current === 'status' ? STEPS.length : activeIndex} sx={{ my: 4 }}>
        {STEPS.map((s) => (
          <Step key={s.slug}>
            <StepLabel>{s.label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      <Card>
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          {current === 'details' && <DetailsStep mine={mine} onSubmitted={afterSubmit} />}
          {current === 'sample' && <SampleStep onSubmitted={afterSubmit} />}
          {current === 'status' && <StatusStep mine={mine} />}
        </CardContent>
      </Card>
    </Container>
  );
});
